import { ReactElement, useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../services/hooks";
import { isAuthSelector } from "../../services/selectors/auth.selectors";
import { adminSelector } from "../../services/selectors/user.selectors";
import { logout } from "../../services/slices/auth.slice";

// Super admin, company admin, AdminTech — the roles the backend issues an admin token to.
const ADMIN_ROLE_IDS = [-1, 0, -3];

interface IProtectedRoot {
  children: ReactElement;
  redirectPath?: string;
}
function ProtectedRoute({
  children,
  redirectPath = "/login",
}: IProtectedRoot): ReactElement {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isAuth = useAppSelector(isAuthSelector);
  const admin = useAppSelector(adminSelector);

  // Second line of defence: the backend refuses non-admin logins, but if any login path ever
  // lets one through (a guardian once got in this way), sign them out instead of rendering the
  // admin app. The role is unknown until the user is loaded, so only a known non-admin is refused.
  const roleId = admin?.roleId;
  const isKnownNonAdmin =
    roleId !== undefined && roleId !== null && !ADMIN_ROLE_IDS.includes(Number(roleId));

  useEffect(() => {
    if (isAuth && isKnownNonAdmin) {
      dispatch(logout());
    }
  }, [isAuth, isKnownNonAdmin, dispatch]);

  return isAuth && !isKnownNonAdmin ? (
    children
  ) : (
    <Navigate to={redirectPath} state={{ from: location.pathname }} replace />
  );
}

export default ProtectedRoute;
